import { useState, useEffect } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { CalendarIcon, Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface OnboardingRecord {
  _id: Id<"clientOnboarding">;
  businessId: Id<"businesses">;
  serviceLocationId?: Id<"serviceLocations">;
  businessName: string;
  serviceLocationName?: string;
  startDate: string;
  dueDate: string;
  assignedTo: Id<"users">;
  assignedUserName: string;
  status: "not-started" | "in-progress" | "completed";
  notes?: string;
}

interface EditClientOnboardingSheetProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onboarding: OnboardingRecord;
  onUpdated?: () => void;
}

export default function EditClientOnboardingSheet({
  open,
  onOpenChange,
  onboarding,
  onUpdated,
}: EditClientOnboardingSheetProps) {
  const [serviceLocationId, setServiceLocationId] = useState(onboarding.serviceLocationId || "");
  const [assignedTo, setAssignedTo] = useState(onboarding.assignedTo);
  const [startDate, setStartDate] = useState<Date | undefined>(
    onboarding.startDate ? new Date(onboarding.startDate) : undefined
  );
  const [dueDate, setDueDate] = useState<Date | undefined>(
    onboarding.dueDate ? new Date(onboarding.dueDate) : undefined
  );
  const [status, setStatus] = useState(onboarding.status);
  const [notes, setNotes] = useState(onboarding.notes || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Fetch real data
  const users = useQuery(api.users.list) || [];
  const allServiceLocations = useQuery(api.serviceLocations.getServiceLocations) || [];
  const updateOnboarding = useMutation(api.clientOnboarding.update);

  // Filter service locations for the same business
  const serviceLocations = allServiceLocations.filter(
    (loc: any) => loc.businessId === onboarding.businessId
  );
  const selectedLocation = allServiceLocations.find((loc: any) => loc._id === serviceLocationId);

  // Reset form when onboarding data changes
  useEffect(() => {
    setServiceLocationId(onboarding.serviceLocationId || "");
    setAssignedTo(onboarding.assignedTo);
    setStartDate(onboarding.startDate ? new Date(onboarding.startDate) : undefined);
    setDueDate(onboarding.dueDate ? new Date(onboarding.dueDate) : undefined);
    setStatus(onboarding.status);
    setNotes(onboarding.notes || "");
  }, [onboarding]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!serviceLocationId || !assignedTo || !startDate || !dueDate) {
      toast({
        title: "Error",
        description: "Please fill in all required fields including service location",
        variant: "destructive",
      });
      return;
    }

    if (!selectedLocation) {
      toast({
        title: "Error",
        description: "Please select a valid service location",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await updateOnboarding({
        id: onboarding._id,
        serviceLocationId: serviceLocationId as Id<"serviceLocations">,
        startDate: format(startDate, "yyyy-MM-dd"),
        dueDate: format(dueDate, "yyyy-MM-dd"),
        assignedTo: assignedTo as Id<"users">,
        status: status,
        notes: notes || undefined,
      });

      toast({
        title: "Success!",
        description: "Onboarding updated successfully!",
      });

      if (onUpdated) onUpdated();
      if (onOpenChange) onOpenChange(false);
    } catch (error) {
      console.error("Error updating onboarding:", error);
      toast({
        title: "Error",
        description: "Failed to update onboarding record. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto sm:max-w-md md:max-w-lg">
        <SheetHeader>
          <SheetTitle>Edit Service Location Onboarding</SheetTitle>
          <SheetDescription>
            Update the onboarding details for{" "}
            {onboarding.serviceLocationName || "this service location"}.
          </SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="serviceLocationId">
              Service Location <span className="text-red-500">*</span>
            </Label>
            <Select value={serviceLocationId} onValueChange={setServiceLocationId} required>
              <SelectTrigger>
                <SelectValue placeholder="Select a service location" />
              </SelectTrigger>
              <SelectContent>
                {serviceLocations.map((location: any) => (
                  <SelectItem key={location._id} value={location._id}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedLocation && (
            <div className="space-y-2">
              <Label>Client</Label>
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                <p className="text-sm font-medium text-gray-900">{selectedLocation.businessName}</p>
                <p className="text-xs text-gray-500">Auto-selected based on service location</p>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="assignedTo">
              Assigned To <span className="text-red-500">*</span>
            </Label>
            <Select value={assignedTo} onValueChange={(val) => setAssignedTo(val as any)} required>
              <SelectTrigger>
                <SelectValue placeholder="Select a team member" />
              </SelectTrigger>
              <SelectContent>
                {users.map((user: any) => (
                  <SelectItem key={user._id} value={user._id}>
                    {user.name || user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={status}
              onValueChange={(value) =>
                setStatus(value as "not-started" | "in-progress" | "completed")
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not-started">Not Started</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="startDate">
              Service Start Date <span className="text-red-500">*</span>
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, "PPP") : "Select start date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={setStartDate}
                  initialFocus
                  className="rounded-md border"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dueDate">
              Due Date <span className="text-red-500">*</span>
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, "PPP") : "Select due date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={setDueDate}
                  initialFocus
                  className="rounded-md border"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any special instructions or notes for this onboarding..."
              rows={3}
            />
          </div>

          <SheetFooter className="pt-4">
            <Button type="submit" disabled={isSubmitting} className="w-full">
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Onboarding"
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
